import clsx from 'clsx'
import type {
  ChangeEvent,
  ChangeEventHandler,
  PropsWithChildren,
  Ref,
} from 'react'
import React, { useEffect, useRef, useState } from 'react'
import { useInitialRender } from '../hooks/useInitialRender'
import { useSyncRefs } from '../hooks/useSyncRefs'
import CheckBoxChecked from '../icons/CheckBoxChecked'
import CheckBoxIndeterminate from '../icons/CheckBoxIndeterminate'
import CheckBoxUnchecked from '../icons/CheckBoxUnchecked'

export interface CheckboxProps {
  /**
   * Accessible label for the checkbox input
   */
  'aria-label'?: string
  /**
   * The color scheme of the checkbox.
   * Affects the checkbox color and hover/focus states
   * @default 'primary'
   */
  color?: CheckboxColor
  /**
   * When true, reduces padding around the checkbox for tighter spacing
   */
  constrained?: boolean
  /**
   * Used for column filters in Table component
   */
  'data-key'?: string
  /**
   * Initial checked state of the checkbox
   * @default false
   */
  defaultChecked?: boolean
  /**
   * If true, the checkbox is disabled
   * @default false
   */
  disabled?: boolean
  /**
   * Additional descriptive text to display below the checkbox label
   */
  helperText?: string
  /** When true, displays the checkbox in an indeterminate state (partially checked)
   * @default false
   */
  indeterminate?: boolean
  /**
   * Reference to the underlying input element
   * @default null
   */
  inputRef?: Ref<HTMLInputElement>
  /**
   * The name attribute for the checkbox input element
   */
  name?: string
  /**
   * The size of the checkbox
   * @default 'small'
   */
  size?: CheckboxSize
  /** Callback function triggered when the checkbox state changes */
  onChange?: ChangeEventHandler<HTMLInputElement>
  /**
   * Tab order index for keyboard navigation
   */
  tabIndex?: number
}

type CheckboxColor = 'error' | 'primary' | 'secondary'

type CheckboxColorType =
  | 'default'
  | 'disabled'
  | 'focused'
  | 'color'
  | 'unchecked'

type CheckboxColors = {
  [key in CheckboxColor]: { [key in CheckboxColorType]: string }
}

type CheckboxStatus = 'unchecked' | 'checked' | 'indeterminate'

type CheckboxSize = 'medium' | 'small'

type CheckboxSizes = {
  [key in 'constrained' | 'default']: { [key in CheckboxSize]: string }
}

const colors: CheckboxColors = {
  primary: {
    default: 'text-primary-main hover:bg-primary-10 active:bg-primary-20',
    disabled: 'text-black-30 bg-transparent',
    focused: 'bg-primary-10',
    color: 'text-primary-main',
    unchecked: 'text-black-60',
  },
  secondary: {
    default: 'text-secondary-main hover:bg-secondary-10 active:bg-secondary-20',
    disabled: 'text-black-30 bg-transparent',
    focused: 'bg-secondary-10',
    color: 'text-secondary-main',
    unchecked: 'text-black-60',
  },
  error: {
    default: 'text-error-main hover:bg-error-10 active:bg-error-20',
    disabled: 'text-black-30 bg-transparent',
    focused: 'bg-error-10',
    color: 'text-error-main',
    unchecked: 'text-black-60',
  },
}

const sizes: CheckboxSizes = {
  constrained: {
    medium: 'w-6 h-6',
    small: 'w-5 h-5',
  },
  default: {
    medium: 'w-10 h-10 p-2',
    small: 'w-9 h-9 p-2',
  },
}

function Input({
  'aria-label': label,
  children,
  color = 'primary',
  constrained,
  'data-key': key,
  defaultChecked = false,
  disabled = false,
  helperText,
  indeterminate = false,
  inputRef = null,
  name,
  onChange,
  size = 'small',
  tabIndex,
}: PropsWithChildren<CheckboxProps>): JSX.Element {
  let initialStatus: CheckboxStatus
  if (indeterminate) {
    initialStatus = 'indeterminate'
  } else {
    initialStatus = defaultChecked ? 'checked' : 'unchecked'
  }

  const initialRender = useInitialRender()
  const ref = useRef<HTMLInputElement | null>(null)
  const [focused, setFocused] = useState(false)
  const checkboxRef = useSyncRefs(ref, inputRef)

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event)
    }
  }
  const handleBlur = () => setFocused(false)
  const handleFocus = () => setFocused(true)

  useEffect(() => {
    if (ref.current && !initialRender) {
      ref.current.checked = defaultChecked
    }
    // We are not supplying initialRender to the dependency array since its value is part
    // of a reference. When included, it resets the checked property to defaultChecked
    // after the checkbox is clicked programmatically for the first time
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultChecked])

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate
    }
    // We are not supplying initialRender to the dependency array since its value is part
    // of a reference. When included, it resets the checked property to defaultChecked
    // after the checkbox is clicked programmatically for the first time
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indeterminate])

  return (
    <label
      className={clsx(
        'checkbox flex flex-col font-sans',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
      )}
    >
      <div className="flex items-center">
        <span
          className={clsx(
            'shrink-0 rounded-full focus:outline-none relative',
            disabled ? colors[color].disabled : colors[color].default,
            focused ? colors[color].focused : null,
            constrained ? sizes.constrained[size] : sizes.default[size],
          )}
        >
          <input
            aria-label={label}
            className={clsx(
              'absolute inset-0 w-full h-full p-0 m-0 opacity-0',
              disabled ? 'cursor-not-allowed' : 'cursor-pointer',
            )}
            data-key={key}
            defaultChecked={initialStatus === 'checked'}
            disabled={disabled}
            name={name}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            ref={checkboxRef}
            tabIndex={tabIndex}
            type="checkbox"
          />
          <span
            className={clsx(
              'status-active w-full h-full',
              disabled ? colors[color].disabled : colors[color].color,
            )}
          >
            <CheckBoxChecked />
          </span>
          <span
            className={clsx(
              'status-indeterminate w-full h-full',
              disabled ? colors[color].disabled : colors[color].color,
            )}
          >
            <CheckBoxIndeterminate />
          </span>
          <span
            className={clsx(
              'status-inactive w-full h-full',
              disabled ? colors[color].disabled : colors[color].unchecked,
            )}
          >
            <CheckBoxUnchecked />
          </span>
        </span>
        {children ? (
          <div
            className={clsx(
              'text-sm font-normal select-none',
              disabled ? 'text-black-45' : 'text-black-90',
              constrained ? 'pl-2' : '',
            )}
          >
            {children}
          </div>
        ) : null}
      </div>
      {children ? (
        <div
          className={clsx(
            'text-xs font-normal tracking-wid',
            disabled ? 'text-black-30' : 'text-black-60',
          )}
        >
          {helperText ? helperText : null}
        </div>
      ) : null}
    </label>
  )
}

export default Input
